/**
 * ☰ › Admin › **Group requests**: read what was asked, write down what was
 * agreed, and approve it as a group — or decline it with a reason the
 * requester will see (`docs/plans/group-requests.md` §2, the approved text
 * mock of 2026-10-12):
 *
 *   Group requests                                       pending 3 · approved 5 · declined 1
 *     Trader Club   ana.business@example.com   wants O   ~40 members   2 days ago   [ Open ]
 *
 *   Trader Club — contact ana.business@example.com  [ Copy ]   +91 …
 *                 account ana@example.com (01J…, QuFolio)
 *     What it's for       … word for word …
 *     What's unique       … word for word …
 *     Notes (yours only)  [ call 14 Oct — wants 12%, monthly, INR                ]
 *     Approve as          ( ) Q — QuFolio sells it   (•) O — they sell it
 *     QuFolio's cut       (•) %  [ 12 ]    ( ) fixed $ [     ] per payment
 *     [ Approve & create group ]   [ Decline… ]
 *
 * **Both addresses, because they answer different questions.** The contact
 * email is where the negotiation goes — hence Copy beside it; the account is
 * who the group will belong to, and whom approval makes its Group Admin.
 *
 * **What was agreed is written back here before anything is made.** The notes
 * are required to approve, and so is the sale mode and, for O, the cut — the
 * plan's rule that a group never goes live on terms that exist only in an
 * inbox. The button says why it is disabled rather than silently refusing;
 * the server holds the same rule and is the one that decides.
 *
 * Brand-scoped on the server: an admin sees only their own brand's requests,
 * so nothing here filters by brand.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import {
  GROUP_KEY_PATTERN,
  approveGroupRequest,
  declineGroupRequest,
  getGroupRequest,
  listGroupRequests,
  saveGroupRequestNotes,
  suggestGroupKey,
  type AdminGroupRequest,
  type AdminGroupRequestList,
  type CutKind,
  type RequestStatus,
  type SellAs,
} from "../api/groupRequests";

type Filter = RequestStatus | "all";

const FILTERS: Filter[] = ["pending", "approved", "declined", "all"];

/** A percent or an amount: positive, at most two decimals. The range is checked apart. */
const CUT_SHAPE = /^\d+(\.\d{1,2})?$/;

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "12px 0 4px",
};

const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative)" };

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "4px 0",
  flexWrap: "wrap",
};

const GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "130px 1fr",
  gap: "6px 10px",
  alignItems: "start",
};

const LABEL: React.CSSProperties = { color: "var(--qf-text-secondary)", paddingTop: 2 };

const WORDS: React.CSSProperties = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" };

const INPUT: React.CSSProperties = {
  font: "inherit",
  padding: "2px 6px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  minWidth: 0,
};

const BUTTON: React.CSSProperties = {
  font: "inherit",
  padding: "2px 10px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const DETAIL: React.CSSProperties = {
  marginTop: 8,
  paddingTop: 8,
  borderTop: "1px solid var(--qf-border)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

function filterStyle(active: boolean): React.CSSProperties {
  return {
    ...BUTTON,
    border: "1px solid",
    borderColor: active ? "var(--qf-accent)" : "transparent",
    color: active ? "var(--qf-text)" : "var(--qf-text-secondary)",
    padding: "1px 6px",
  };
}

/** "2 days ago", from an ISO timestamp. Coarse on purpose: this is a queue. */
export function ageOf(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(0, Math.floor((now - then) / 60_000));
  if (minutes < 60) return minutes <= 1 ? "just now" : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "an hour ago" : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

/**
 * Why the approve button is not yet available, or `null` when it is. The
 * server holds the same rules; this only saves a round trip and says which.
 */
export function approvalProblem(input: {
  key: string;
  name: string;
  notes: string;
  sellAs: SellAs;
  cutKind: CutKind;
  cutValue: string;
}): string | null {
  if (!input.notes.trim()) return "Write down what was agreed in Notes first.";
  if (!input.name.trim()) return "The group needs a name.";
  if (!GROUP_KEY_PATTERN.test(input.key)) {
    return "The key is grp- then lowercase letters, digits and hyphens.";
  }
  if (input.sellAs === "o") {
    const value = input.cutValue.trim();
    if (!value) return "Set QuFolio's cut.";
    if (!CUT_SHAPE.test(value) || Number(value) <= 0) {
      return "The cut is a positive number, at most two decimals.";
    }
    if (input.cutKind === "percent" && Number(value) > 100) return "A percentage is at most 100.";
  }
  return null;
}

async function copy(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function GroupRequestsAdmin() {
  const [filter, setFilter] = useState<Filter>("pending");
  const [list, setList] = useState<AdminGroupRequestList | null | undefined>(undefined);
  const [openKey, setOpenKey] = useState<string | null>(null);

  // **Only the newest ask may land.** Click "all" then "pending" quickly and
  // the slower answer is the older one; applied, it puts decided requests in
  // the pending queue under a pressed "pending" button.
  const latest = useRef(0);
  const reload = useCallback(() => {
    const ask = ++latest.current;
    void listGroupRequests(filter).then((next) => {
      if (ask === latest.current) setList(next);
    });
  }, [filter]);
  useEffect(reload, [reload]);

  if (list === undefined) return null;
  if (list === null) return <div style={NOTE}>Group requests could not be loaded.</div>;

  const { counts } = list;

  return (
    <>
      <div style={{ ...ROW, justifyContent: "space-between" }}>
        <div style={SECTION}>Group requests</div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {FILTERS.map((name) => (
            <button
              key={name}
              type="button"
              aria-pressed={filter === name}
              style={filterStyle(filter === name)}
              onClick={() => {
                setFilter(name);
                setOpenKey(null);
              }}
            >
              {name === "all"
                ? `all ${counts.pending + counts.approved + counts.declined}`
                : `${name} ${counts[name]}`}
            </button>
          ))}
        </div>
      </div>

      {list.requests.length === 0 ? (
        <div style={NOTE}>No {filter === "all" ? "" : `${filter} `}requests.</div>
      ) : (
        list.requests.map((request) => (
          <div key={request.key} style={ROW} data-testid="group-request-row">
            <strong>{request.name}</strong>
            <span>{request.contactEmail}</span>
            <span style={NOTE}>wants {request.sellAs.toUpperCase()}</span>
            {request.expectedMembers !== null && (
              <span style={NOTE}>~{request.expectedMembers} members</span>
            )}
            <span style={MUTED}>{ageOf(request.createdAt)}</span>
            {filter === "all" && <span style={MUTED}>{request.status}</span>}
            <button
              type="button"
              style={BUTTON}
              onClick={() => setOpenKey(openKey === request.key ? null : request.key)}
            >
              {openKey === request.key ? "Close" : "Open"}
            </button>
          </div>
        ))
      )}

      {openKey && (
        <RequestDetail
          key={openKey}
          requestKey={openKey}
          onChanged={reload}
        />
      )}
    </>
  );
}

function RequestDetail({
  requestKey,
  onChanged,
}: {
  requestKey: string;
  onChanged: () => void;
}) {
  const [request, setRequest] = useState<AdminGroupRequest | null | undefined>(undefined);
  const [notes, setNotes] = useState("");
  const [sellAs, setSellAs] = useState<SellAs>("q");
  const [cutKind, setCutKind] = useState<CutKind>("percent");
  const [percent, setPercent] = useState("");
  const [fixed, setFixed] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [groupName, setGroupName] = useState("");
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    void getGroupRequest(requestKey).then((answer) => {
      if (!alive) return;
      setRequest(answer);
      if (answer) {
        setNotes(answer.adminNotes ?? "");
        setSellAs(answer.sellAs);
        setGroupKey(suggestGroupKey(answer.name));
        setGroupName(answer.name);
      }
    });
    return () => {
      alive = false;
    };
  }, [requestKey]);

  if (request === undefined) return null;
  if (request === null) return <div style={DETAIL}><span style={NOTE}>That request could not be loaded.</span></div>;

  const pending = request.status === "pending";
  const cutValue = cutKind === "percent" ? percent : fixed;
  const problem = approvalProblem({ key: groupKey, name: groupName, notes, sellAs, cutKind, cutValue });

  const run = async (work: () => Promise<AdminGroupRequest>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      setRequest(next);
      setNotes(next.adminNotes ?? "");
      onChanged();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Saved on blur, and apart from `run`: a blur is usually the click on
  // Approve arriving, and disabling the buttons for the save would eat it.
  const saveNotes = () => {
    if (notes === (request.adminNotes ?? "")) return;
    setError(null);
    void saveGroupRequestNotes(request.key, notes).then(
      (next) => setRequest((current) => (current ? { ...current, adminNotes: next.adminNotes } : current)),
      (cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)),
    );
  };

  const approve = () => {
    if (problem) return;
    void run(() =>
      approveGroupRequest(request.key, {
        key: groupKey,
        name: groupName.trim(),
        sellAs,
        notes: notes.trim(),
        ...(sellAs === "o" ? { cutKind, cutValue: cutValue.trim() } : {}),
      }),
    );
  };

  const decline = async () => {
    if (!reason.trim()) return;
    if (await run(() => declineGroupRequest(request.key, reason.trim()))) setDeclining(false);
  };

  const { account } = request;

  return (
    <div style={DETAIL} data-testid="group-request-detail">
      <div style={ROW}>
        <strong>{request.name}</strong>
        <span style={NOTE}>— contact</span>
        <span>{request.contactEmail}</span>
        <button
          type="button"
          style={BUTTON}
          onClick={() => void copy(request.contactEmail).then(setCopied)}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        {request.contactPhone && <span>{request.contactPhone}</span>}
      </div>
      <div style={NOTE}>
        account {account.email ?? "no email on file"} ({account.key}
        {account.displayName ? `, ${account.displayName}` : ""}, {request.appKey})
      </div>

      <div style={GRID}>
        <span style={LABEL}>What it's for</span>
        <div style={WORDS}>{request.purpose}</div>

        <span style={LABEL}>What's unique</span>
        <div style={WORDS}>{request.uniqueFeatures}</div>

        <span style={LABEL}>They'd sell it</span>
        <div>
          {request.sellAs === "q" ? "QuFolio sells it" : "They sell it"}
          {request.expectedMembers !== null && (
            <span style={NOTE}> · ~{request.expectedMembers} members</span>
          )}
        </div>

        <label style={LABEL} htmlFor="grq-notes">Notes (yours only)</label>
        <textarea
          id="grq-notes"
          style={{ ...INPUT, minHeight: 48, resize: "vertical" }}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          onBlur={saveNotes}
        />

        {pending && (
          <>
            <span style={LABEL}>Approve as</span>
            <div role="radiogroup" aria-label="Approve as" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <label>
                <input type="radio" name="grq-sell" checked={sellAs === "q"} onChange={() => setSellAs("q")} />{" "}
                Q — QuFolio sells it
              </label>
              <label>
                <input type="radio" name="grq-sell" checked={sellAs === "o"} onChange={() => setSellAs("o")} />{" "}
                O — they sell it
              </label>
            </div>

            {sellAs === "o" && (
              <>
                <span style={LABEL}>QuFolio's cut</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <label>
                    <input
                      type="radio"
                      name="grq-cut"
                      checked={cutKind === "percent"}
                      onChange={() => setCutKind("percent")}
                    />{" "}
                    %
                  </label>
                  <input
                    aria-label="Cut percent"
                    style={{ ...INPUT, width: 60 }}
                    inputMode="decimal"
                    value={percent}
                    disabled={cutKind !== "percent"}
                    onChange={(event) => setPercent(event.target.value)}
                  />
                  <label>
                    <input
                      type="radio"
                      name="grq-cut"
                      checked={cutKind === "fixed"}
                      onChange={() => setCutKind("fixed")}
                    />{" "}
                    fixed $
                  </label>
                  <input
                    aria-label="Cut amount"
                    style={{ ...INPUT, width: 80 }}
                    inputMode="decimal"
                    value={fixed}
                    disabled={cutKind !== "fixed"}
                    onChange={(event) => setFixed(event.target.value)}
                  />
                  <span style={NOTE}>per payment</span>
                </div>
              </>
            )}

            <label style={LABEL} htmlFor="grq-key">Group key</label>
            <input
              id="grq-key"
              style={{ ...INPUT, width: 260 }}
              value={groupKey}
              onChange={(event) => setGroupKey(event.target.value.trim().toLowerCase())}
            />

            <label style={LABEL} htmlFor="grq-name">Group name</label>
            <input
              id="grq-name"
              style={{ ...INPUT, width: 260 }}
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
            />
          </>
        )}

        {!pending && (
          <>
            <span style={LABEL}>Decided</span>
            <div>
              {request.status === "approved" ? "Approved" : "Declined"}
              {request.decidedAt && ` ${request.decidedAt.slice(0, 10)}`}
              {request.decidedBy && <span style={NOTE}> by {request.decidedBy}</span>}
              {request.orgKey && (
                <>
                  {" — "}
                  <code>{request.orgKey}</code>
                </>
              )}
            </div>
            {request.declineReason && (
              <>
                <span style={LABEL}>Reason</span>
                <div style={WORDS}>{request.declineReason}</div>
              </>
            )}
          </>
        )}
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}

      {pending && !declining && (
        <>
          <div style={ROW}>
            <button
              type="button"
              style={{ ...BUTTON, opacity: problem || busy ? 0.5 : 1 }}
              disabled={problem !== null || busy}
              onClick={approve}
            >
              Approve &amp; create group
            </button>
            <button type="button" style={BUTTON} disabled={busy} onClick={() => setDeclining(true)}>
              Decline…
            </button>
          </div>
          {problem && <div style={NOTE}>{problem}</div>}
        </>
      )}

      {pending && declining && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={LABEL} htmlFor="grq-reason">Reason (they will see it)</label>
          <textarea
            id="grq-reason"
            style={{ ...INPUT, minHeight: 40, resize: "vertical" }}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div style={ROW}>
            <button
              type="button"
              style={{ ...BUTTON, opacity: reason.trim() && !busy ? 1 : 0.5 }}
              disabled={!reason.trim() || busy}
              onClick={() => void decline()}
            >
              Decline request
            </button>
            <button type="button" style={BUTTON} onClick={() => setDeclining(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
